import {
	Autocomplete,
	Box,
	Button,
	Card,
	CardContent,
	CircularProgress,
	Paper,
	TextField,
	Typography
} from "@mui/material";
import { useFormik } from "formik";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import * as Yup from "yup";
import { useFreelancerContext } from "../../../contexts/useFreelancerContext";
import { useNavigationInterceptorContext } from "../../../contexts/useNavigationInterceptorContext";
import {
	usePatchMySectorsFreelancerOnboardingStep,
	useSaveMySectorsFreelancerOnboardingProgress
} from "../../../hooks/useFreelancerProfile";
import { useBusinessDomains, useSectors } from "../../../hooks/useReferential";
import { FreelancerOnboardingFormStep } from "../../../models/enums/FreelancerOnboardingFormStep";
import { zoullooColors } from "../../../theme";
import {
	FreelancerOnboardingBusinessDomainViewModel,
	FreelancerOnboardingMySectorsStepViewModel,
	FreelancerOnboardingSectorViewModel
} from "../../../ViewModels/FreelancerOnboardingMySectorsStepViewModel";
import { FreelancerOnboardingStatus } from "../../../models/enums/FreelancerOnboardingStatus";

export default function FreelancerOnboardingMySectors() {
	const {
		setFreelancerOnboardingMySectorsStepViewModel,
		setCurrentOnboardingFormStep,
		setNextOnboardingFormStep: setNextOnboardingStep,
		freelancerProfileQueryResult
	} = useFreelancerContext();
	const { data: freelancerProfile, isLoading: isFreelancerProfileLoading } =
		freelancerProfileQueryResult;
	const { data: sectors, isLoading: isSectorsLoading } = useSectors();
	const { data: businessDomains, isLoading: isBusinessDomainsLoading } =
		useBusinessDomains();
	const patchMySectorsFreelancerOnboarding = usePatchMySectorsFreelancerOnboardingStep();
	const saveMySectorsFreelancerOnboardingProgression =
		useSaveMySectorsFreelancerOnboardingProgress({ hideAllSnackbars: true });
	const { t } = useTranslation();
	const { interceptNavigation } = useNavigationInterceptorContext();

	const maxSectors = 5;
	const maxBusinessDomains = 5;

	const formValidation = Yup.object({
		sectorViewModels: Yup.array()
			.min(1, t("freelancerOnboardingMySectors.sectors.required"))
			.max(maxSectors, t("freelancerOnboardingMySectors.sectors.maxReached")),
		businessDomainViewModels: Yup.array()
			.min(1, t("freelancerOnboardingMySectors.businessDomains.required"))
			.max(
				maxBusinessDomains,
				t("freelancerOnboardingMySectors.businessDomains.maxReached")
			)
	});

	const initialValues = {
		sectorViewModels:
			freelancerProfile?.sectors.map(
				x =>
					({
						id: x.id,
						sector: { id: x.sector.id, label: x.sector.label }
					} as FreelancerOnboardingSectorViewModel)
			) ?? [],
		businessDomainViewModels:
			freelancerProfile?.businessDomains.map(
				x =>
					({
						id: x.id,
						businessDomain: { id: x.businessDomain.id, label: x.businessDomain.label }
					} as FreelancerOnboardingBusinessDomainViewModel)
			) ?? [],
		form: null
	} as FreelancerOnboardingMySectorsStepViewModel;

	const formik = useFormik<FreelancerOnboardingMySectorsStepViewModel>({
		initialValues,
		onSubmit: async values => {
			try {
				await patchMySectorsFreelancerOnboarding.mutateAsync({
					freelancerOnboardingMySectorsStepViewModel: values,
					freelancerProfile: freelancerProfile!,
					newStep:
						freelancerProfile?.onboarding.currentStatus ===
						FreelancerOnboardingStatus.Accepted
							? FreelancerOnboardingFormStep.MySectors
							: FreelancerOnboardingFormStep.MyPresentation
				});

				if (
					freelancerProfile?.onboarding.currentStatus !==
					FreelancerOnboardingStatus.Accepted
				) {
					setCurrentOnboardingFormStep(FreelancerOnboardingFormStep.MyPresentation);
				}
			} catch (error) {
				console.error("Error submitting form:", error);
				formik.setFieldValue("form.isSubmitting", false);
			}
		},
		validationSchema: formValidation,
		enableReinitialize: true
	});

	useEffect(() => {
		setFreelancerOnboardingMySectorsStepViewModel({
			...formik.values,
			form: {
				isValid: formValidation.isValidSync(formik.values),
				isSubmitting: formik.isSubmitting,
				isDirty: formik.dirty
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [formik.values]);

	return (
		<form onSubmit={formik.handleSubmit}>
			<Card
				component={Paper}
				elevation={0}
				sx={{
					margin: "32px"
				}}
			>
				<CardContent sx={{ p: 4 }}>
					<Typography variant="h2" component="h1" sx={{ mb: 2, fontWeight: "bold" }}>
						{t("freelancerOnboardingMySectors.title")}
					</Typography>
					<Typography
						variant="body1"
						marginBottom={"30px"}
						sx={{ whiteSpace: "pre-line" }}
					>
						{t("freelancerOnboardingMySectors.description")}
					</Typography>

					<Box sx={{ mb: 3 }}>
						<Box>
							<Typography variant="h5" marginBottom={"10px"}>
								{t("freelancerOnboardingMySectors.sectors.label")}
							</Typography>

							<Typography variant="body1" marginBottom={"10px"}>
								<Trans
									i18nKey={"freelancerOnboardingMySectors.sectors.description"}
									components={{
										strong: <strong />
									}}
								/>
							</Typography>
						</Box>
						<Autocomplete
							multiple={true}
							disableCloseOnSelect={true}
							loading={isSectorsLoading || isFreelancerProfileLoading}
							isOptionEqualToValue={(option, value) => option.id === value.id}
							options={isSectorsLoading || isFreelancerProfileLoading ? [] : sectors!}
							getOptionLabel={e => t(`Sectors.${e.label}`)}
							getOptionDisabled={option => {
								const selectedSectorIds = formik.values.sectorViewModels.map(
									x => x.sector.id
								);
								const isAlreadySelected = selectedSectorIds.includes(option.id);
								const hasReachedMax = formik.values.sectorViewModels.length >= maxSectors;
								return !isAlreadySelected && hasReachedMax;
							}}
							value={formik.values.sectorViewModels.map(x => x.sector) ?? []}
							onChange={(_, newValue) => {
								formik.setFieldValue(
									"sectorViewModels",
									newValue.map(
										sector =>
											({
												id: formik.values.sectorViewModels.filter(
													x => x.sector.id === sector.id
												)?.[0]?.id,
												sector
											} as FreelancerOnboardingSectorViewModel)
									)
								);

								formik.setTouched({
									sectorViewModels: formik.values.sectorViewModels.map(() => ({
										id: formik.values.sectorViewModels.length - 1 > maxSectors,
										sector: {
											id: formik.values.sectorViewModels.length - 1 > maxSectors,
											label: formik.values.sectorViewModels.length - 1 > maxSectors
										}
									}))
								});
							}}
							renderOption={(props, option) => {
								const selectedSectorIds = formik.values.sectorViewModels.map(
									x => x.sector.id
								);
								const isAlreadySelected = selectedSectorIds.includes(option.id);
								const hasReachedMax = formik.values.sectorViewModels.length >= maxSectors;
								const isDisabled = !isAlreadySelected && hasReachedMax;

								return (
									<Box
										component="li"
										{...props}
										sx={{
											color: isDisabled ? "#9e9e9e !important" : "inherit",
											backgroundColor: isDisabled ? "#f5f5f5 !important" : "inherit",
											pointerEvents: isDisabled ? "none" : "auto",
											opacity: isDisabled ? 0.6 : 1
										}}
									>
										{t(`Sectors.${option.label}`)}
									</Box>
								);
							}}
							onBlur={() => {
								formik.setTouched({
									sectorViewModels: formik.values.sectorViewModels.map(() => ({
										id: true,
										sector: {
											id: true,
											label: true
										}
									}))
								});
							}}
							renderInput={params => (
								<TextField
									{...params}
									placeholder={t("freelancerOnboardingMySectors.sectors.placeholder")}
									variant="outlined"
									error={
										formik.touched.sectorViewModels &&
										(formik.touched.sectorViewModels.length === 0 ||
											formik.touched.sectorViewModels?.some(
												x => x.sector?.id === true || x.sector?.label === true
											) === true) &&
										Boolean(formik.errors.sectorViewModels as string)
									}
									helperText={
										formik.touched.sectorViewModels &&
										(formik.touched.sectorViewModels.length === 0 ||
											formik.touched.sectorViewModels?.some(
												x => x.sector?.id === true || x.sector?.label === true
											) === true) &&
										(formik.errors.sectorViewModels as string)
									}
									sx={{
										width: "45%"
									}}
									slotProps={{
										input: {
											...params.InputProps,
											endAdornment: (
												<>
													{(isSectorsLoading || isFreelancerProfileLoading) && (
														<CircularProgress size={25} />
													)}
													{params.InputProps?.endAdornment}
												</>
											)
										}
									}}
								/>
							)}
							sx={{ mb: 2 }}
						/>
					</Box>

					<Box sx={{ mb: 3 }}>
						<Box>
							<Typography variant="h5" marginBottom={"10px"}>
								{t("freelancerOnboardingMySectors.businessDomains.label")}
							</Typography>

							<Typography variant="body1" marginBottom={"10px"}>
								<Trans
									i18nKey={"freelancerOnboardingMySectors.businessDomains.description"}
									components={{
										strong: <strong />
									}}
								/>
							</Typography>
						</Box>
						<Autocomplete
							multiple={true}
							disableCloseOnSelect={true}
							loading={isBusinessDomainsLoading || isFreelancerProfileLoading}
							isOptionEqualToValue={(option, value) => option.id === value.id}
							options={
								isBusinessDomainsLoading || isFreelancerProfileLoading
									? []
									: businessDomains!
							}
							getOptionLabel={e => t(`BusinessDomains.${e.label}`)}
							getOptionDisabled={option => {
								const selectedBusinessDomainIds =
									formik.values.businessDomainViewModels.map(x => x.businessDomain.id);
								const isAlreadySelected = selectedBusinessDomainIds.includes(option.id);
								const hasReachedMax =
									formik.values.businessDomainViewModels.length >= maxBusinessDomains;
								return !isAlreadySelected && hasReachedMax;
							}}
							value={
								formik.values.businessDomainViewModels.map(x => x.businessDomain) ?? []
							}
							onChange={(_, newValue) => {
								formik.setFieldValue(
									"businessDomainViewModels",
									newValue.map(
										businessDomain =>
											({
												id: formik.values.businessDomainViewModels.filter(
													x => x.businessDomain.id === businessDomain.id
												)?.[0]?.id,
												businessDomain
											} as FreelancerOnboardingBusinessDomainViewModel)
									)
								);

								formik.setTouched({
									businessDomainViewModels: formik.values.businessDomainViewModels.map(
										() => ({
											id:
												formik.values.businessDomainViewModels.length - 1 >
												maxBusinessDomains,
											businessDomain: {
												id:
													formik.values.businessDomainViewModels.length - 1 >
													maxBusinessDomains,
												label:
													formik.values.businessDomainViewModels.length - 1 >
													maxBusinessDomains
											}
										})
									)
								});
							}}
							onBlur={() => {
								formik.setTouched({
									businessDomainViewModels: formik.values.businessDomainViewModels.map(
										() => ({
											id: true,
											businessDomain: {
												id: true,
												label: true
											}
										})
									)
								});
							}}
							renderOption={(props, option) => {
								const selectedBusinessDomainIds =
									formik.values.businessDomainViewModels.map(x => x.businessDomain.id);
								const isAlreadySelected = selectedBusinessDomainIds.includes(option.id);
								const hasReachedMax =
									formik.values.businessDomainViewModels.length >= maxBusinessDomains;
								const isDisabled = !isAlreadySelected && hasReachedMax;

								return (
									<Box
										component="li"
										{...props}
										sx={{
											color: isDisabled ? "#9e9e9e !important" : "inherit",
											backgroundColor: isDisabled ? "#f5f5f5 !important" : "inherit",
											pointerEvents: isDisabled ? "none" : "auto",
											opacity: isDisabled ? 0.6 : 1
										}}
									>
										{t(`BusinessDomains.${option.label}`)}
									</Box>
								);
							}}
							renderInput={params => (
								<TextField
									{...params}
									placeholder={t(
										"freelancerOnboardingMySectors.businessDomains.placeholder"
									)}
									variant="outlined"
									error={
										formik.touched.businessDomainViewModels &&
										(formik.touched.businessDomainViewModels.length === 0 ||
											formik.touched.businessDomainViewModels?.some(
												x =>
													x.businessDomain?.id === true ||
													x.businessDomain?.label === true
											) === true) &&
										Boolean(formik.errors.businessDomainViewModels as string)
									}
									helperText={
										formik.touched.businessDomainViewModels &&
										(formik.touched.businessDomainViewModels.length === 0 ||
											formik.touched.businessDomainViewModels?.some(
												x =>
													x.businessDomain?.id === true ||
													x.businessDomain?.label === true
											) === true) &&
										(formik.errors.businessDomainViewModels as string)
									}
									sx={{
										width: "45%"
									}}
									slotProps={{
										input: {
											...params.InputProps,
											endAdornment: (
												<>
													{(isBusinessDomainsLoading || isFreelancerProfileLoading) && (
														<CircularProgress size={25} />
													)}
													{params.InputProps?.endAdornment}
												</>
											)
										}
									}}
								/>
							)}
							sx={{ mb: 2 }}
						/>
					</Box>
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							justifyContent: "center",
							gap: 3,
							mt: 2
						}}
					>
						<Button
							type="submit"
							variant="contained"
							color="secondary"
							disabled={!formValidation.isValidSync(formik.values) || formik.isSubmitting}
							sx={{
								color: zoullooColors.secondary.contrastText,
								width: "40%",
								mt: 8
							}}
						>
							{freelancerProfile?.onboarding.currentStatus !==
							FreelancerOnboardingStatus.Accepted
								? t("freelancerOnboardingMySectors.buttons.submit")
								: t("common.save")}
						</Button>
						{freelancerProfile?.onboarding.currentStatus !==
							FreelancerOnboardingStatus.Accepted && (
							<Button
								variant="outlined"
								color="secondary"
								sx={{
									width: "40%"
								}}
								onClick={async () => {
									interceptNavigation({
										onValidNavigation: async () => {
											try {
												await saveMySectorsFreelancerOnboardingProgression.mutateAsync({
													freelancerProfile: freelancerProfile!,
													freelancerOnboardingMySectorsStepViewModel: formik.values,
													newStep: FreelancerOnboardingFormStep.MySkills
												});

												setCurrentOnboardingFormStep(
													FreelancerOnboardingFormStep.MySkills
												);
											} catch (error) {
												console.error(
													"Error returning back to the previous step:",
													error
												);
											}
										},
										onBlockedNavigation: () => {
											setNextOnboardingStep(FreelancerOnboardingFormStep.MySkills);
										}
									});
								}}
							>
								{t("freelancerOnboardingMySectors.buttons.back")}
							</Button>
						)}
					</Box>
				</CardContent>
			</Card>
		</form>
	);
}
